import { ComponentType, LazyExoticComponent, Suspense } from "react";
import React from "react";
import ErrorBoundary from "../ErrorBoundary";
import { Switch, Route } from "react-router";
import { IRouteConfig } from "services/typings";
import { SwitchProps } from "react-router-dom";
import PageSkeleton from "components/base/PageSkeleton";
import RouteErrorBoundary from "components/base/RouteErrorBoundary";
import * as Sentry from "@sentry/react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import Box from "components/base/Box";

const SentryRoute = Sentry.withSentryRouting(Route);

export const withErrorBoundary = (
  fn: () => Promise<{ default: ComponentType<any> }>,
  retriesLeft = 3,
  interval = 1000
): Promise<{ default: ComponentType<any> }> => {
  return new Promise((resolve, reject) => {
    fn()
      .then(({ default: Component }: any) => {
        resolve({
          default: (props: any) => (
            <RouteErrorBoundary>
              <Component {...props} />
            </RouteErrorBoundary>
          ),
        });
      })
      .catch((error: any) => {
        reject(error);
      });
  });
};

export type PreloadableComponent<T extends LazyExoticComponent<any>> = T & {
  preload: () => Promise<void>;
};

const loadedComponentMap = new Map();

export const lazy = <T extends ComponentType<any>>(
  fn: () => Promise<{ default: T }>
): PreloadableComponent<any> => {
  const Component = React.lazy(() => withErrorBoundary(fn));
  (Component as any).preload = () => {
    const loaded = loadedComponentMap.get(fn);
    if (loaded) return;
    loadedComponentMap.set(fn, true);
    return withErrorBoundary(fn) as any;
  };
  return Component as any;
};

export const renderRoutes = (
  routes?: IRouteConfig[],
  extraProps: object = {},
  switchProps: SwitchProps = {}
) => {
  return routes ? (
    <Switch {...switchProps}>
      {routes.map((route, i) => {
        const Component = route?.component as any;
        const { suspense = true } = route;
        const Placeholder = route?.placeholder as any;
        return (
          <SentryRoute
            key={route.key || i}
            path={route.path}
            exact={route.exact}
            strict={route.strict}
            render={(props) => (
              <>
                {suspense ? (
                  <Suspense
                    fallback={
                      !!Placeholder ? (
                        <Placeholder />
                      ) : (
                        <div onClick={() => console.log("onclick")}>
                          <PageSkeleton
                            title={route?.name}
                            {...(route?.placeholderConfig || {})}
                          />
                        </div>
                      )
                    }
                  >
                    {route.render ? (
                      route.render({
                        ...props,
                        ...extraProps,
                        route: route,
                      })
                    ) : (
                      <Component {...props} {...extraProps} route={route} />
                    )}
                  </Suspense>
                ) : route.render ? (
                  route.render({ ...props, ...extraProps, route: route })
                ) : (
                  <Component {...props} {...extraProps} route={route} />
                )}
              </>
            )}
          />
        );
      })}
    </Switch>
  ) : null;
};
